<template>
  <div class="ocr-report-validation">
    <div class="ocr-report-map-toolbar">
      <v-btn size="small" color="primary" variant="flat" :loading="loading" @click="runValidation">
        Run validation
      </v-btn>
      <v-btn
        v-if="canSaveFixes"
        size="small"
        variant="outlined"
        color="primary"
        :loading="savingFixes"
        @click="saveFixes"
      >
        Save mapping fixes
      </v-btn>
    </div>
    <v-alert v-if="!ran" type="info" variant="tonal" density="compact">
      Validates mapped column and header data against tenant configuration (Streamlit Review/Saved checks).
    </v-alert>
    <v-alert
      v-else-if="!hasErrors"
      type="success"
      variant="tonal"
      density="compact"
    >
      No validation errors reported.
    </v-alert>
    <template v-else>
      <section v-if="validation.no_data" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Data</h4>
        <p>{{ validation.no_data }}</p>
      </section>
      <section v-if="validation.table_type?.length" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Table requirements</h4>
        <ul class="ocr-validation-list">
          <li v-for="(err, i) in validation.table_type" :key="'tt' + i">
            <strong>{{ err.column }}</strong> — {{ err.message }}
          </li>
        </ul>
      </section>
      <section v-if="validation.uom?.length" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">UOM</h4>
        <ul class="ocr-validation-list">
          <li v-for="(err, i) in validation.uom" :key="'uom' + i">{{ err }}</li>
        </ul>
      </section>
      <section v-if="validation.data_type?.length" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Data type</h4>
        <ul class="ocr-validation-list">
          <li v-for="(err, i) in validation.data_type" :key="'dt' + i">
            <strong>{{ err.column }}</strong> — {{ err.message }}
          </li>
        </ul>
      </section>
      <section v-if="validation.enumeration?.length" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Enumeration</h4>
        <div v-for="(err, i) in validation.enumeration" :key="'en' + i" class="ocr-validation-fix-row">
          <span class="ocr-validation-fix-label">{{ err.column }}: {{ err.value }}</span>
          <v-select
            v-model="enumFixes[i]"
            :items="err.options || []"
            :menu-props="{ contentClass: 'ocr-select-menu' }"
            density="compact"
            variant="outlined"
            hide-details
            placeholder="Map to member"
            class="ocr-report-column-select"
          />
        </div>
      </section>
      <section v-if="validation.component?.length" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Components</h4>
        <div v-for="(err, i) in validation.component" :key="'comp' + i" class="ocr-validation-fix-row">
          <span class="ocr-validation-fix-label">{{ err.component || err }}</span>
          <v-select
            v-model="componentFixes[i]"
            :items="err.component_options || []"
            :menu-props="{ contentClass: 'ocr-select-menu' }"
            density="compact"
            variant="outlined"
            hide-details
            placeholder="Standard component"
            class="ocr-report-column-select"
          />
        </div>
      </section>
      <section v-if="validation.composition_sum" class="ocr-validation-section">
        <h4 class="ocr-validation-heading">Composition</h4>
        <p>{{ validation.composition_sum }}</p>
      </section>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrReportValidationPanel",
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
  },
  emits: ["updated", "status"],
  data() {
    return {
      loading: false,
      savingFixes: false,
      ran: false,
      hasErrors: false,
      validation: {},
      enumFixes: {},
      componentFixes: {},
    };
  },
  computed: {
    canSaveFixes() {
      return this.ran && (this.pendingEnumMappings.length || this.pendingComponentMappings.length);
    },
    pendingEnumMappings() {
      const out = [];
      (this.validation.enumeration || []).forEach((err, i) => {
        const member = this.enumFixes[i];
        if (member && err.enumeration_set) {
          out.push({
            enumeration_set: err.enumeration_set,
            member,
            mapped_value: err.value,
          });
        }
      });
      return out;
    },
    pendingComponentMappings() {
      const out = [];
      (this.validation.component || []).forEach((err, i) => {
        const mapped = this.componentFixes[i];
        const original = err.component || "";
        if (mapped && original) {
          out.push({ original_component: original, mapped_component: mapped });
        }
      });
      return out;
    },
  },
  watch: {
    filename: { handler() { this.reset(); } },
    tableIndex: { handler() { this.reset(); } },
  },
  methods: {
    reset() {
      this.ran = false;
      this.hasErrors = false;
      this.validation = {};
      this.enumFixes = {};
      this.componentFixes = {};
    },
    applyValidationResult(data) {
      this.validation = data.validation || {};
      this.hasErrors = !!data.has_errors;
      this.ran = true;
      this.enumFixes = {};
      this.componentFixes = {};
      if (data.table) this.$emit("updated", data.table);
    },
    async runValidation() {
      if (!this.filename || this.tableIndex == null) return;
      this.loading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/validation`,
        );
        this.applyValidationResult(data);
        this.$emit("status", {
          type: this.hasErrors ? "warning" : "success",
          message: this.hasErrors ? "Validation found issues." : "Validation passed.",
        });
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Validation failed.") });
      } finally {
        this.loading = false;
      }
    },
    async saveFixes() {
      if (!this.filename || this.tableIndex == null) return;
      this.savingFixes = true;
      try {
        let last = null;
        if (this.pendingEnumMappings.length) {
          const { data } = await ocrApi.post(
            `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/validation/enumerations`,
            { mappings: this.pendingEnumMappings },
          );
          last = data;
        }
        if (this.pendingComponentMappings.length) {
          const { data } = await ocrApi.post(
            `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/validation/components`,
            { mappings: this.pendingComponentMappings },
          );
          last = data;
        }
        if (last) this.applyValidationResult(last);
        this.$emit("status", { type: "success", message: "Mapping fixes saved; validation re-run." });
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not save fixes.") });
      } finally {
        this.savingFixes = false;
      }
    },
  },
};
</script>
